import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteTaskRelation } from '@/lib/api/generated/api';
import type { DirectTaskRelationsResponse } from '@/lib/api/generated/model';
import { mapTaskRelationError } from '../errors';
import { taskRelationsKeys } from '../queryKeys';

export interface RemoveTaskRelationInput {
  relationId: string;
  /** Both sides of the removed edge, so both cached `.direct` lists can drop it. */
  taskIds: readonly [string, string];
}

/**
 * Wraps generated `deleteTaskRelation`, throwing the person-facing message
 * (research D5) rather than the raw domain error on any non-204 response —
 * both widget modes (staged and persisted) consume this directly.
 */
export function useRemoveTaskRelation(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      relationId,
    }: RemoveTaskRelationInput): Promise<void> => {
      const response = await deleteTaskRelation(workspaceId, relationId);
      if (response.status !== 204) {
        throw new Error(
          mapTaskRelationError(
            'error' in response.data ? response.data : undefined,
          ),
        );
      }
    },
    onSuccess: (_data, { relationId, taskIds }) => {
      for (const taskId of taskIds) {
        queryClient.setQueryData(
          taskRelationsKeys.direct(workspaceId, taskId),
          (prev: DirectTaskRelationsResponse | undefined) =>
            prev
              ? {
                  ...prev,
                  relations: prev.relations.filter((r) => r.id !== relationId),
                }
              : prev,
        );
        // The `.related` graph (depth/relationship/workflow grouping) can't
        // be patched client-side, but the global QueryClient's default
        // staleTime (60s, src/lib/queryClient.ts) means `enabled: open`
        // toggling true on reopen won't refetch on its own within that
        // window unless the query is explicitly marked stale here.
        void queryClient.invalidateQueries({
          queryKey: taskRelationsKeys.related(workspaceId, taskId),
        });
      }
    },
  });
}
